import {
    createAsyncThunk,
    createSlice,
    isAnyOf,
    PayloadAction,
} from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import { AppState } from '../../reducers/root.reducer';
import Admin360QuestionnaireService from '../services/Admin360QuestionnaireService';
import ProjectQuestionnaire from '../types/project/ProjectQuestionnaire';
import { RequestServiceResponse } from '@tcc-online/request-service';
import ProjectTranslation from '../types/project/ProjectTranslation';
import Section from '../types/project/360/Section';
import Subsection from '../types/project/360/Subsection';
import Question from '../types/project/360/Question';
import IPaginationState from '../../types/state/IPaginationState';

const NAME = 'Admin360Questionnaire';

const getQuestionnaires = createAsyncThunk(
    `${NAME}/getQuestionnaires`,
    Admin360QuestionnaireService.getQuestionnaires,
);
const getQuestionnaire = createAsyncThunk(
    `${NAME}/getQuestionnaire`,
    Admin360QuestionnaireService.getQuestionnaire,
);
const createQuestionnaire = createAsyncThunk(
    `${NAME}/createQuestionnaire`,
    Admin360QuestionnaireService.createQuestionnaire,
);
const updateQuestionnaire = createAsyncThunk(
    `${NAME}/updateQuestionnaire`,
    Admin360QuestionnaireService.updateQuestionnaire,
);

const getSections = createAsyncThunk(
    `${NAME}/getSections`,
    Admin360QuestionnaireService.getSections,
);
const getSection = createAsyncThunk(
    `${NAME}/getSection`,
    Admin360QuestionnaireService.getSection,
);
const createSection = createAsyncThunk(
    `${NAME}/createSection`,
    Admin360QuestionnaireService.createSection,
);
const updateSection = createAsyncThunk(
    `${NAME}/updateSection`,
    Admin360QuestionnaireService.updateSection,
);

const getSubsections = createAsyncThunk(
    `${NAME}/getSubsections`,
    Admin360QuestionnaireService.getSubsections,
);
const getSubsection = createAsyncThunk(
    `${NAME}/getSubsection`,
    Admin360QuestionnaireService.getSubsection,
);
const createSubsection = createAsyncThunk(
    `${NAME}/createSubsection`,
    Admin360QuestionnaireService.createSubsection,
);
const updateSubsection = createAsyncThunk(
    `${NAME}/updateSubsection`,
    Admin360QuestionnaireService.updateSubsection,
);

const getQuestions = createAsyncThunk(
    `${NAME}/getQuestions`,
    Admin360QuestionnaireService.getQuestions,
);
const getQuestion = createAsyncThunk(
    `${NAME}/getQuestion`,
    Admin360QuestionnaireService.getQuestion,
);
const createQuestion = createAsyncThunk(
    `${NAME}/createQuestion`,
    Admin360QuestionnaireService.createQuestion,
);
const updateQuestion = createAsyncThunk(
    `${NAME}/updateQuestion`,
    Admin360QuestionnaireService.updateQuestion,
);
const deleteQuestion = createAsyncThunk(
    `${NAME}/deleteQuestion`,
    Admin360QuestionnaireService.deleteQuestion,
);

const getTranslations = createAsyncThunk(
    `${NAME}/getTranslations`,
    Admin360QuestionnaireService.getTranslations,
);
const saveTranslations = createAsyncThunk(
    `${NAME}/saveTranslations`,
    Admin360QuestionnaireService.saveTranslations,
);
const addLanguage = createAsyncThunk(
    `${NAME}/addLanguage`,
    Admin360QuestionnaireService.addLanguage,
);

// const getTranslation = createAsyncThunk(
//     `${NAME}/getTranslation`,
//     Admin360QuestionnaireService.getTranslation,
// );
// const createTranslation = createAsyncThunk(
//     `${NAME}/createTranslation`,
//     Admin360QuestionnaireService.createTranslation,
// );
// const updateTranslation = createAsyncThunk(
//     `${NAME}/updateSuTranslation`,
//     Admin360QuestionnaireService.updateTranslation,
// );

export interface Admin360QuestionnaireState {
    list: IPaginationState<ProjectQuestionnaire[]>;
    detail: IBaseState<ProjectQuestionnaire | null>;
    sections: IBaseState<Section[]>;
    translations: IBaseState<ProjectTranslation[]>;
}

const initialState: Admin360QuestionnaireState = {
    list: {
        data: [],
        loading: false,
        pagination: { page: 1 },
    },
    detail: {
        data: null,
        loading: false,
    },
    sections: {
        data: [],
        loading: false,
    },
    translations: {
        data: [],
        loading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder): void => {
        builder.addCase(
            getQuestionnaires.pending,
            (state: Admin360QuestionnaireState): void => {
                state.list.loading = true;
            },
        );
        builder.addCase(
            getQuestionnaires.fulfilled,
            (
                state: Admin360QuestionnaireState,
                action: PayloadAction<
                    RequestServiceResponse<ProjectQuestionnaire[]>
                >,
            ): void => {
                state.list.data = action.payload.data;
                state.list.pagination = (action.payload as any).pagination;
            },
        );

        builder.addCase(
            getQuestionnaire.pending,
            (state: Admin360QuestionnaireState): void => {
                state.detail.loading = true;
            },
        );
        builder.addCase(
            getQuestionnaire.fulfilled,
            (
                state: Admin360QuestionnaireState,
                action: PayloadAction<
                    RequestServiceResponse<ProjectQuestionnaire>
                >,
            ): void => {
                state.detail.data = action.payload.data;
            },
        );

        builder.addCase(
            getSections.fulfilled,
            (
                state: Admin360QuestionnaireState,
                action: PayloadAction<RequestServiceResponse<Section[]>>,
            ): void => {
                state.sections.data = action.payload.data;
            },
        );

        builder.addCase(
            getSubsections.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const sectionId: number = action.meta.arg.sectionId;
                if (!state.sections.data) {
                    return;
                }

                const index: number = findSectionIndex(
                    state.sections.data,
                    sectionId,
                );

                if (index < 0) {
                    return;
                }

                state.sections.data[index].subsections = action.payload.data;
            },
        );

        builder.addCase(
            getQuestions.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const { sectionId, subsectionId } = action.meta.arg;
                if (!state.sections.data) {
                    return;
                }

                const sectionIndex: number = findSectionIndex(
                    state.sections.data,
                    sectionId,
                );
                const subsectionIndex: number = findSubsectionIndex(
                    state.sections.data,
                    sectionIndex,
                    subsectionId,
                );
                if (subsectionIndex < 0) {
                    return;
                }

                state.sections.data[sectionIndex].subsections[
                    subsectionIndex
                ].questions = action.payload.data;
            },
        );

        builder.addCase(
            updateSection.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const sectionId: number = action.meta.arg.sectionId;
                const index: number = findSectionIndex(
                    state.sections.data,
                    sectionId,
                );
                if (index >= 0) {
                    state.sections.data[index] = action.payload.data;
                }
            },
        );

        builder.addCase(
            updateSubsection.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const { sectionId, subsectionId } = action.meta.arg;

                if (!state.sections.data) {
                    return;
                }

                const sectionIndex: number = findSectionIndex(
                    state.sections.data,
                    sectionId,
                );
                const subsectionIndex: number = findSubsectionIndex(
                    state.sections.data,
                    sectionIndex,
                    subsectionId,
                );

                if (subsectionIndex < 0) {
                    return;
                }

                state.sections.data[sectionIndex].subsections[subsectionIndex] =
                    action.payload.data;
            },
        );

        builder.addCase(
            updateQuestion.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const { sectionId, subsectionId, questionId } = action.meta.arg;

                const questions = findQuestionsInSections(
                    state.sections.data,
                    sectionId,
                    subsectionId,
                );
                if (!questions || !questions.length) {
                    return;
                }

                const questionIndex: number = findQuestionIndex(
                    questions,
                    questionId,
                );
                if (questionIndex < 0) {
                    return;
                }

                questions[questionIndex] = action.payload.data;
            },
        );

        builder.addCase(
            deleteQuestion.fulfilled,
            (state: Admin360QuestionnaireState, action): void => {
                const { sectionId, subsectionId, questionId } = action.meta.arg;

                const questions = findQuestionsInSections(
                    state.sections.data,
                    sectionId,
                    subsectionId,
                );
                if (!questions || !questions.length) {
                    return;
                }

                const questionIndex: number = findQuestionIndex(
                    questions,
                    questionId,
                );
                if (questionIndex < 0) {
                    return;
                }

                questions.splice(questionIndex, 1);
            },
        );

        builder.addCase(
            getTranslations.pending,
            (state: Admin360QuestionnaireState): void => {
                state.translations.loading = true;
            },
        );
        builder.addCase(
            getTranslations.fulfilled,
            (
                state: Admin360QuestionnaireState,
                action: PayloadAction<
                    RequestServiceResponse<ProjectTranslation[]>
                >,
            ): void => {
                state.translations.data = action.payload.data;
            },
        );
        builder.addCase(
            addLanguage.fulfilled,
            (state: Admin360QuestionnaireState, action) => {
                if (state.detail.data) {
                    state.detail.data.languages = action.payload.data.languages;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(getQuestionnaires.fulfilled, getQuestionnaires.rejected),
            (state: Admin360QuestionnaireState): void => {
                state.list.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getQuestionnaire.fulfilled, getQuestionnaire.rejected),
            (state: Admin360QuestionnaireState): void => {
                state.detail.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getSections.pending,
                updateSection.pending,
                getSubsections.pending,
            ),
            (state: Admin360QuestionnaireState): void => {
                state.sections.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getSections.fulfilled,
                getSections.rejected,
                updateSection.fulfilled,
                updateSection.rejected,
                getSubsections.fulfilled,
                getSubsections.rejected,
            ),
            (state: Admin360QuestionnaireState): void => {
                state.sections.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getTranslations.fulfilled, getTranslations.rejected),
            (state: Admin360QuestionnaireState): void => {
                state.translations.loading = false;
            },
        );
    },
});

const state = (state: AppState): Admin360QuestionnaireState =>
    state.admin['360'].questionnaire;

const Admin360QuestionnaireSlice = {
    reducer: slice.reducer,
    actions: {
        getQuestionnaires,
        getQuestionnaire,
        createQuestionnaire,
        updateQuestionnaire,
        getSections,
        getSection,
        createSection,
        updateSection,
        getSubsections,
        getSubsection,
        createSubsection,
        updateSubsection,
        getQuestions,
        getQuestion,
        createQuestion,
        updateQuestion,
        getTranslations,
        deleteQuestion,
        saveTranslations,
        addLanguage,
    },
    state: {
        all: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state,
        ),
        list: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state.list,
        ),
        detail: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state.detail,
        ),
        sections: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state.sections,
        ),
        translations: createSelector(
            state,
            (state: Admin360QuestionnaireState) => state.translations,
        ),
        translationsByLocale: createSelector(
            [state, (state: AppState, locale: string) => locale],
            (state: Admin360QuestionnaireState, locale: string) =>
                state.translations?.data?.[locale],
        ),
    },
};

export default Admin360QuestionnaireSlice;

function findSectionIndex(sections: Section[], sectionId: number) {
    return sections.findIndex(
        (section: Section): boolean => section.id === sectionId,
    );
}

function findSubsectionIndex(
    sections: Section[],
    sectionIndex: number,
    subsectionId: number,
) {
    if (sectionIndex < 0 || !sections[sectionIndex]?.subsections) {
        return -1;
    }

    return sections[sectionIndex].subsections.findIndex(
        (subsection: Subsection): boolean => subsection.id === subsectionId,
    );
}

function findQuestionIndex(questions: Question[], questionId: number) {
    return questions.findIndex(
        (question: Question) => question.id === questionId,
    );
}

function findQuestionsInSections(
    sections: Section[],
    sectionId: number,
    subsectionId: number,
) {
    if (!sections) {
        return;
    }

    const sectionIndex: number = findSectionIndex(sections, sectionId);
    const subsectionIndex: number = findSubsectionIndex(
        sections,
        sectionIndex,
        subsectionId,
    );
    if (subsectionIndex < 0) {
        return;
    }

    const questions: Question[] =
        sections[sectionIndex].subsections[subsectionIndex].questions;
    if (!questions) {
        return;
    }

    return sections[sectionIndex].subsections[subsectionIndex].questions;
}
